import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { OwnerVoiceFormControls, useOwnerVoiceForm, voiceFieldProps } from "../voice/OwnerVoiceOperatorContext";

function normalizeLocalDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

export default function OwnerWorkLogDialog({ open, occurredAt = "", onClose, onSaved }) {
  const [form, setForm] = React.useState({ title: "", minutes: "60", occurredAt: "", note: "" });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setForm({ title: "", minutes: "60", occurredAt: normalizeLocalDateTime(occurredAt || new Date()), note: "" });
    setError("");
  }, [open, occurredAt]);

  const voice = useOwnerVoiceForm({
    id: "worklog",
    open: Boolean(open),
    config: {
      title: "trabajo real",
      values: form,
      setValue: (name, value) => setForm((current) => ({ ...current, [name]: value })),
      fields: [
        { name: "title", label: "qué hiciste", prompt: "Contame qué hiciste.", required: true, aliases: ["trabajo", "tarea", "que hice", "qué hice"] },
        { name: "minutes", label: "minutos reales", prompt: "¿Cuántos minutos reales trabajaste?", kind: "number", required: true, aliases: ["minutos", "tiempo", "duracion", "duración"] },
        { name: "occurredAt", label: "cuándo", prompt: "¿Cuándo lo hiciste? Podés decir ahora, hoy a las dos o una fecha y hora.", kind: "datetime", required: true, aliases: ["cuando", "cuándo", "hora", "fecha"] },
        { name: "note", label: "nota", prompt: "¿Querés agregar una nota? Si no, decí siguiente.", required: false, aliases: ["nota", "detalle"] },
      ],
      onSubmit: () => submit(),
      onCancel: onClose,
      successMessage: "Trabajo registrado.",
    },
  });

  async function submit() {
    const title = form.title.trim();
    const minutes = Number(form.minutes);
    if (!title) { setError("Contame qué hiciste."); return false; }
    if (!Number.isFinite(minutes) || minutes < 1 || minutes > 1440) { setError("El tiempo debe estar entre 1 minuto y 24 horas."); return false; }
    const when = new Date(form.occurredAt);
    if (Number.isNaN(when.getTime())) { setError("La hora del trabajo no es válida."); return false; }

    setSaving(true);
    setError("");
    try {
      const created = await ownerApi.createCommitment({
        title,
        kind: "task",
        priority: "normal",
        dueAt: when.toISOString(),
        estimatedMinutes: Math.round(minutes),
        notes: `${form.note.trim()}${form.note.trim() ? "\n" : ""}[work-log]`,
        source: { kind: voice?.handsFree ? "voice" : "manual" },
      });
      const commitmentId = idOf(created?.commitment);
      if (!commitmentId) throw new Error("No pude identificar el trabajo creado.");
      await ownerApi.addCommitmentTime(commitmentId, {
        minutes: Math.round(minutes),
        occurredAt: when.toISOString(),
        note: form.note.trim(),
        source: { kind: voice?.handsFree ? "voice" : "manual" },
      });
      await ownerApi.updateCommitment(commitmentId, { status: "done" });
      await onSaved?.();
      onClose?.();
      return true;
    } catch (err) {
      setError(formatApiError(err, "No pude registrar lo que hiciste."));
      return false;
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={Boolean(open)} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 1000 }}>Registrar lo que hiciste</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: "text.secondary", fontWeight: 720, fontSize: 12.5, mb: 1.2 }}>
          Esto llena tu día real. Sirve aunque no hubieras creado una tarea antes.
        </Typography>
        {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
        <Stack spacing={1.15}>
          <TextField inputProps={voiceFieldProps("title")} autoFocus fullWidth label="Qué hiciste" placeholder="Ej. trabajé una changa, llamé clientes, mejoré WebProfit" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <TextField inputProps={voiceFieldProps("occurredAt")} fullWidth type="datetime-local" label="Cuándo" InputLabelProps={{ shrink: true }} value={form.occurredAt} onChange={(event) => setForm({ ...form, occurredAt: event.target.value })} />
            <TextField fullWidth type="number" label="Minutos reales" inputProps={{ min: 1, max: 1440, step: 5, ...voiceFieldProps("minutes") }} value={form.minutes} onChange={(event) => setForm({ ...form, minutes: event.target.value })} />
          </Stack>
          <TextField inputProps={voiceFieldProps("note")} fullWidth multiline minRows={2} label="Nota opcional" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
          <OwnerVoiceFormControls label="Completar registro hablando" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>{saving ? "Guardando…" : "Guardar trabajo real"}</Button>
      </DialogActions>
    </Dialog>
  );
}
